import { Injectable } from '@angular/core';
import { ApiService, IInvoiceInfo, SPECIAL_USER_LOGIN_TYPES } from '@cakeiteasy/web-utils';
import { IDeliveryAddress, IUser } from '../../users';
import { Observable, firstValueFrom } from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';
import extend from 'lodash/extend';
import moment from 'moment';

@Injectable()
export class UserApiService {
  constructor(private _api: ApiService) {
  }

  public getLoginType(params: { email: string, bakeryId: number }): Observable<{ loginType: SPECIAL_USER_LOGIN_TYPES }> {
    return this._api
      .post('api.users', 'login_type', null, { ...params });
  }

  public login(credentials): Promise<IUser> {
    return firstValueFrom(this._api
      .post('api.users', 'login', {}, credentials));
  }

  public fetchCurrentUser(): Observable<IUser> {
    return this._api
      .get('api.users', 'current');
  }

  public register(user) {
    return firstValueFrom(this._api
      .post('api.users', 'register', {}, user));
  }

  public logout() {
    return firstValueFrom(this._api
      .get('api.users', 'logout'));
  }

  public getOrders() {
    return firstValueFrom(this._api
      .get('api.users', 'orders'));
  }

  public getDeliveryAddresses() {
    return firstValueFrom(this._api
      .get('api.users', 'delivery_addresses'));
  }

  public reminderGroups(method: string, groupId?: number, body?: any): Promise<any> {
    let path = groupId ? 'reminderCalendars_one' : 'reminderCalendars';

    return firstValueFrom(this._api[method]('api.users', path, { calendarId: groupId }, body));
  }

  public reminderGroupsReminder(method: string, groupId: number, reminderId?: number, body?: any): Promise<any> {
    let path = reminderId ? 'reminderCalendars_one_reminders_one' : 'reminderCalendars_one_reminders';

    let model = cloneDeep(body);
    if (model) {
      model.targetDate = moment(model.targetDate).format('YYYY-MM-DD');
    }

    return firstValueFrom(this._api[method]('api.users', path, { calendarId: groupId, reminderId }, model));
  }

  public saveInvoiceInfo(invoiceInfo: IInvoiceInfo): Observable<IInvoiceInfo> {
    const serverModel: Omit<IInvoiceInfo, 'company'> & { company: number } = {
      ...cloneDeep(invoiceInfo),
      company: invoiceInfo.company.id
    };

    if (!invoiceInfo.id) {
      return this._api.post('users', 'current_invoiceInfo', {}, serverModel);
    }

    return this._api.put('users', 'current_invoiceInfo', {}, serverModel);
  }

  public removeInvoiceInfo(): Observable<any> {
    return this._api.remove('users', 'current_invoiceInfo');
  }

  public updateInfo(userInfo: IUser) {
    const payload = extend({}, userInfo);
    return this._api.patch('api.users', 'current_user', {}, payload);
  }

  public partialUserUpdate(userInfo: any) {
    return this._api.patch('api.users', 'current_user', {}, userInfo);
  }

  public addDeliveryAddress(deliveryAddress: IDeliveryAddress): Promise<IDeliveryAddress> {
    return firstValueFrom(this._api.post('api.users', 'delivery_addresses', {}, deliveryAddress));
  }

  public updateDeliveryAddress(deliveryAddress: IDeliveryAddress) {
    return firstValueFrom(this._api.put('api.users', 'delivery_addresses_one',
      { deliveryAddressId: deliveryAddress.id },
      deliveryAddress));
  }

  public setFavouriteDeliveryAddress(deliveryAddressId: number) {
    return firstValueFrom(this._api.patch('api.users', 'delivery_addresses_favorite', { deliveryAddressId }));
  }

  public deleteDeliveryAddress(deliveryAddressId) {
    return firstValueFrom(this._api.remove('api.users', 'delivery_addresses_one', { deliveryAddressId }));
  }

  public activateByPaymentSid(sid, data) {
    return firstValueFrom(this._api.post('api.users', 'activate_by_payment_sid', { sid }, data));
  }

  public resetPassword(data) {
    return this._api.patch('api.users', 'password', {}, data);
  }

  public restorePassword(data) {
    return this._api.post('api.users', 'restore_password', {}, data);
  }

  public restorePasswordRequest(data) {
    return this._api.post('api.users', 'restore_password_request', {}, data);
  }

  public sendFeedback(data) {
    return firstValueFrom(this._api.post('api.store', 'feedback', {}, data));
  }
}
